import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components/macro";
import Heading from "./Heading";

const bannerBackgrounds = {
  attention: "var(--juno-banner-attention-background)",
  info: "var(--juno-banner-info-background)",
  success: "var(--juno-banner-success-background)",
};

const bannerBorders = {
  attention: "var(--juno-banner-attention-border)",
  info: "var(--juno-banner-info-border)",
  success: "var(--juno-banner-success-border)",
};

// The styled props are transient ($-prefixed) so styled-components never
// forwards them to the div. v5 happens to filter "variant" and "border" out by
// itself, but that is @emotion/is-prop-valid's allowlist deciding for us, and
// v6 drops the automatic filtering for host elements altogether.
//
// Two border treatments, as in webapp-alpha's .juno-panel. A thick left rail
// marks a note that sits in the page. A thin outline all the way round marks a
// notification: the result of something the client did.
const borderStyles = {
  left: css`
    border-left-style: solid;
    border-left-width: var(--juno-banner-border-width);
    border-left-color: ${({ $variant }) => bannerBorders[$variant]};
  `,
  // Longhands, not the border shorthand: a shorthand holding a var() has no
  // readable longhands until substitution, so the tests could not see it.
  outline: css`
    border-style: solid;
    border-top-width: var(--juno-banner-outline-width);
    border-right-width: var(--juno-banner-outline-width);
    border-bottom-width: var(--juno-banner-outline-width);
    border-left-width: var(--juno-banner-outline-width);
    border-top-color: ${({ $variant }) => bannerBorders[$variant]};
    border-right-color: ${({ $variant }) => bannerBorders[$variant]};
    border-bottom-color: ${({ $variant }) => bannerBorders[$variant]};
    border-left-color: ${({ $variant }) => bannerBorders[$variant]};
  `,
};

const StyledBanner = styled.div`
  background-color: ${({ $variant }) => bannerBackgrounds[$variant]};
  ${({ $border }) => borderStyles[$border]}
  padding: var(--juno-space-4);
  border-radius: var(--juno-radius-md);
`;

// The banner's padding already sets the title off from the edge, so the
// heading's own top margin would double it.
const StyledBannerTitle = styled(Heading)`
  margin-top: 0;
  margin-bottom: var(--juno-space-2);
`;

const StyledBannerContent = styled.div`
  display: grid;
  row-gap: var(--juno-space-1);

  p:only-child {
    margin: 0;
  }
`;

// The ref and role are here for a banner that appears in answer to something
// the client did: the caller announces it and moves focus to it, because the
// control the client used has often gone by then.
const Banner = React.forwardRef(function Banner(
  { variant = "info", border = "left", title, role, tabIndex, children },
  ref,
) {
  return (
    <StyledBanner $variant={variant} $border={border} ref={ref} role={role} tabIndex={tabIndex}>
      {title ? (
        <StyledBannerTitle level="2" size="4">
          {title}
        </StyledBannerTitle>
      ) : null}
      <StyledBannerContent>{children}</StyledBannerContent>
    </StyledBanner>
  );
});

export default Banner;

Banner.propTypes = {
  variant: PropTypes.oneOf(["attention", "info", "success"]),
  // "left" for a note in the page, "outline" for a notification.
  border: PropTypes.oneOf(["left", "outline"]),
  // Sits above the children as a subheading. Never an h1: a banner is part of
  // a page, not the page.
  title: PropTypes.string,
  role: PropTypes.string,
  // -1 to make the banner a focus target without putting it in the tab order.
  tabIndex: PropTypes.number,
  children: PropTypes.node,
};
